import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { apiService } from '@/service/api'
import { useTranslation } from '@/i18n'
import type { ConnectAccountsFormData, StoredOAuthState } from '@/types/digital-coworker'

const STORAGE_KEY = 'digital_coworker_onedrive_oauth_state'

/**
 * Store OAuth state before redirecting to Microsoft OAuth.
 * Caller must pass the same `state` in the authorization URL so the callback can verify
 * the response belongs to this request (CSRF protection).
 */
export function storeDigitalCoworkerOneDriveOAuthState(
  coworkerId: string,
  state: string,
  formData?: ConnectAccountsFormData
): void {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ coworkerId, state, formData: formData ?? {} }))
}

function popDigitalCoworkerOneDriveOAuthState(): StoredOAuthState | null {
  const raw = sessionStorage.getItem(STORAGE_KEY)
  sessionStorage.removeItem(STORAGE_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as StoredOAuthState
    if (typeof parsed.coworkerId === 'string' && typeof parsed.state === 'string') {
      return parsed
    }
  } catch {
    // ignore
  }
  return null
}

const DigitalCoworkerOneDriveCallback = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const hasLinkedRef = useRef(false)

  const code = searchParams.get('code')
  const stateFromUrl = searchParams.get('state')

  useEffect(() => {
    if (hasLinkedRef.current) return

    if (!code) {
      toast.error(t('digital_coworker:onedrive_callback.error_auth'))
      navigate('/digital-coworker', { replace: true })
      return
    }

    const stored = popDigitalCoworkerOneDriveOAuthState()
    if (!stored) {
      toast.error(t('digital_coworker:onedrive_callback.error_state'))
      navigate('/digital-coworker', { replace: true })
      return
    }

    if (stateFromUrl !== stored.state) {
      toast.error(t('digital_coworker:onedrive_callback.error_state'))
      navigate('/digital-coworker', { replace: true })
      return
    }

    const { coworkerId } = stored
    hasLinkedRef.current = true
    const redirectUri = `${window.location.origin}/auth/callback/digital-coworker-onedrive`

    const formData = stored.formData ?? {}

    apiService
      .linkDigitalCoworkerMicrosoftAccount(coworkerId, code, redirectUri)
      .then(({ email }) => {
        toast.success(t('digital_coworker:onedrive_callback.success', { email }))
        navigate(`/digital-coworker/connect-accounts`, {
          replace: true,
          state: {
            coworkerId, formData,
            oneDriveConnected: true, oneDriveEmail: email,
            googleConnected: formData.googleConnected ?? false,
            googleEmail: formData.googleEmail ?? null,
          },
        })
      })
      .catch((error: unknown) => {
        console.error('Error linking OneDrive account:', error)
        toast.error(t('digital_coworker:onedrive_callback.error_link'))
        navigate(`/digital-coworker/connect-accounts`, {
          replace: true,
          state: {
            coworkerId, formData,
            oneDriveConnected: false,
            googleConnected: formData.googleConnected ?? false,
            googleEmail: formData.googleEmail ?? null,
          },
        })
      })
  }, [code, stateFromUrl, navigate, t])

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <p className="text-gray-600">{t('digital_coworker:onedrive_callback.connecting')}</p>
      </div>
    </div>
  )
}

export default DigitalCoworkerOneDriveCallback
